import { useState } from "react";
import API, { formatError } from "@/api";
import { toDateTimeLocal } from "@/lib/utils";
import { toast } from "sonner";
import { X, Loader2, Repeat } from "lucide-react";

export default function MakeRecurrentModal({ task, onClose, onDone }) {
    const [recFreq, setRecFreq] = useState("weekly");
    const [recInterval, setRecInterval] = useState(1);
    const [recStart, setRecStart] = useState(() => toDateTimeLocal(new Date().toISOString()));
    const [recSkipIfOpen, setRecSkipIfOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await API.post(`/tasks/${task.id}/make-recurrent`, {
                recurrence: {
                    freq: recFreq,
                    interval: Math.max(1, parseInt(recInterval, 10) || 1),
                    skip_if_open: recSkipIfOpen,
                    next_run: recStart ? new Date(recStart).toISOString() : new Date().toISOString(),
                },
            });
            toast.success("Task transformat în recurent");
            onDone();
            onClose();
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <div className="relative glass-panel rounded-2xl shadow-xl w-full max-w-md mx-4 z-10" data-testid="make-recurrent-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#e3e7f5]">
                    <h2 className="font-bold text-[#111827] text-base flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        <Repeat size={16} className="text-[#0EA5E9]" /> Fă taskul recurent
                    </h2>
                    <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#111827] transition-colors">
                        <X size={18} />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    <p className="text-sm text-[#4B5563]">
                        „<span className="font-semibold text-[#111827]">{task?.title}</span>" devine un task recurent.
                        Titlul, descrierea, responsabilii și checklist-ul se vor copia în fiecare instanță generată automat.
                    </p>
                    <div className="rounded-md border border-[#BAE6FD] bg-[#F0F9FF] p-3 space-y-3">
                        <p className="text-xs font-bold text-[#0369A1] uppercase tracking-wide">Recurență</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Frecvență</label>
                                <select
                                    data-testid="mr-freq"
                                    value={recFreq}
                                    onChange={e => setRecFreq(e.target.value)}
                                    className="w-full px-2 py-2 border border-[#BAE6FD] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white"
                                >
                                    <option value="daily">Zilnic</option>
                                    <option value="weekly">Săptămânal</option>
                                    <option value="monthly">Lunar</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">La fiecare</label>
                                <input
                                    data-testid="mr-interval"
                                    type="number"
                                    min={1}
                                    max={365}
                                    value={recInterval}
                                    onChange={e => setRecInterval(e.target.value)}
                                    className="w-full px-3 py-2 border border-[#BAE6FD] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-[#4B5563] mb-1">Prima generare (data de start)</label>
                            <input
                                data-testid="mr-start"
                                type="datetime-local"
                                value={recStart}
                                onChange={e => setRecStart(e.target.value)}
                                className="w-full px-3 py-2 border border-[#BAE6FD] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] bg-white"
                            />
                        </div>
                        <label className="flex items-start gap-2 text-xs text-[#4B5563] cursor-pointer select-none">
                            <input
                                data-testid="mr-skip-if-open"
                                type="checkbox"
                                checked={recSkipIfOpen}
                                onChange={e => setRecSkipIfOpen(e.target.checked)}
                                className="w-4 h-4 mt-0.5 accent-[#0EA5E9]"
                            />
                            <span>Nu genera o instanță nouă dacă cea precedentă nu e încă în „Finalizat".</span>
                        </label>
                        <p className="text-[11px] text-[#0369A1] leading-snug">
                            La fiecare interval, se creează automat o copie a acestui task în „De făcut". Mută masterul în „Finalizat" pentru a opri seria.
                        </p>
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-[#4B5563] hover:text-[#111827] transition-colors">
                            Anulează
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="mr-submit"
                            className="px-4 py-2 text-sm font-semibold text-white bg-[#0EA5E9] rounded-md hover:bg-[#0284C7] transition-colors disabled:opacity-60 flex items-center gap-2"
                        >
                            {loading && <Loader2 size={14} className="animate-spin" />}
                            Fă recurent
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
